import React, {useMemo, useRef} from 'react';
import AddToProjectModal from "../browse-talent/AddToProjectModal";
import Modal from "../../components/Modal";
import {useTalent} from "../../providers/TalentProvider";
import {useLocation} from "react-use";
import {useProject} from "../../providers/ClientProjectProvider";
import {usePosition} from "../../providers/ProjectPositionProvider";
import {useCandidate} from "../../providers/CandidateProvider";
import {useAuth} from "../../providers/AuthProvider";
import {useIsMutating} from "@tanstack/react-query";
import InfoTooltip from "../../components/InfoTooltip";
import {QuestionCircleFilled, CloseCircleFilled, CheckCircleFilled} from "@ant-design/icons";

const CandidateStatusLabel = ({icon, color, status, className = ' '}) => {
    const StatusIcon = icon;
    return (
        <div className={TB.mapClass("flex flex-row items-center text-sm gap-2", {[className]: true})}>
            <StatusIcon style={{fontSize: 16, color}}/>
            <p>
                {status}
            </p>
        </div>
    )
}

const CandidateRates = (props) => {
    const {
        candidate
    } = useCandidate();

    const standardLabel = candidate.client_override_hourly_rate ? "Preferred Rate" : "Standard Rate";

    let labelClass = `text-base font-semibold pb-1.5 text-wrippleAlternateDarker`;

    return (
        <React.Fragment>
            {candidate.isAssigned() ? (
                <div className="candidate-rates self-stretch">
                    <p className={labelClass}>
                        Accepted Rate
                    </p>
                    <div className="flex flex-row items-center justify-between gap-4 pr-8">
                        <p className="text-[1.5rem] leading-[2rem] font-medium text-wrippleAlternateDarker">
                            <span className="text-xs align-super relative top-[1px]">$</span>
                            <span>
                                {candidate.proposedPlatformRate(false)}
                            </span>
                            <span className="text-sm">
                                /hr
                            </span>
                        </p>
                        {props.children}
                    </div>
                </div>
            ) : candidate.isFixedFee() ? (
                <div className="candidate-rates self-stretch">
                    <p className={labelClass}>
                        Proposed Price
                    </p>
                    <div className="flex flex-row items-center justify-between gap-4 pr-8">
                        <div className="flex flex-row items-center gap-2">
                            <p className="text-[1.5rem] leading-[2rem] font-medium text-wrippleAlternateDarker">
                                {candidate.hasProposalForClient() ? (
                                    <React.Fragment>
                                        {/*<span className="text-xs align-super relative top-[1px]">$</span>*/}
                                        <span>
                                            {candidate.proposedPlatformCost(true)}
                                        </span>
                                    </React.Fragment>
                                ) : "Pending"}
                            </p>
                            <p className="text-xs text-wrippleAlternateDarker">
                                fixed fee
                            </p>
                        </div>
                        {props.children}
                    </div>
                </div>
            ) : (
                <div className="candidate-rates self-stretch">
                    <p className={labelClass}>
                        {candidate.changedHourlyRate() ? 'Proposed Rate' : standardLabel}
                    </p>
                    <div className="flex flex-row items-center justify-between gap-4 pr-8">
                        <div className="flex flex-row items-center gap-2 pb-3">
                            {candidate.changedHourlyRate() && (
                                <p className="text-lg self-center mt-0.5 font-medium text-danger line-through">
                                    <span className="text-black">
                                        {candidate.standardPlatformRate(true)}/hr
                                    </span>
                                </p>
                            )}
                            <p className="font-medium self-center text-wrippleAlternateDarker">
                                <span className="text-sm align-super relative top-[1px]">$</span>
                                <span
                                    className="text-[1.5rem] leading-[2rem]">{candidate.changedHourlyRate() ? candidate.proposedPlatformRate(false) : candidate.standardPlatformRate(false)}</span>
                                <span className="text-sm">
                                    /hr
                                </span>
                            </p>

                        </div>
                        {props.children}
                    </div>
                </div>
            )}
        </React.Fragment>
    )
};

const TalentRates = ({isCandidate, minimized, ...props}) => {
    const {talent} = useTalent();
    const {auth} = useAuth();
    if (!talent) return null;


    return (
        <div className="flex flex-col items-stretch self-stretch">
            {isCandidate ? <CandidateRates {...props}/> : (
                <div>
                    <div id={`talent-rate-type-${talent.id}`}>
                        <p className="text-base font-semibold pb-1.5 text-wrippleAlternateDarker">
                            {auth.isTalent() ? 'Rate' : (talent.client_override_hourly_rate ? "Preferred Rate" : "Standard Rate")}
                        </p>
                        <div className="flex flex-row items-center justify-between gap-4">
                            <p className={TB.mapClass("font-font-medium text-wrippleAlternateDarker", {
                                "text-[1.5rem] leading-[2rem]": !minimized,
                                "text-lg": minimized
                            })}>
                                <span className="text-xs align-super relative top-[1px]">$</span>
                                <span>
                                    {talent?.rangedPlatformRates?.(false, false)}
                                </span>
                                <span className="text-sm">/hr</span>
                            </p>

                            {props.children}
                        </div>
                    </div>
                    {talent.client_override_hourly_rate && (
                        <InfoTooltip target={`talent-rate-type-${talent.id}`} type="dark"
                                     placement="top">
                            <p className="text-xs text-white italic py-1 px-2"
                               style={{textTransform: 'none'}}>
                                This talent offers a preferred rate for your organization
                            </p>
                        </InfoTooltip>
                    )}
                </div>
            )}

        </div>
    )
}
const CandidateActions = ({className = '', minimized, hideRates, ...props}) => {

    const {talent} = useTalent();
    const addToProjectModal = useRef(null);
    const location = useLocation();

    const isBrowse = location.pathname?.includes('browse');
    const isShortlist = location.pathname?.includes('bidding') && location.pathname?.includes('shortlist') || (location.pathname?.includes('active') && location.pathname?.includes('short-list'));
    const isTalentPool = location.pathname?.includes('bidding') && location.pathname?.includes('talent-pool');

    const mutatingRequestsRunning = useIsMutating();

    if (!isBrowse && !isShortlist && !isTalentPool) {
        return <TalentRates minimized={minimized}/>;
    }

    if (isBrowse && talent) {
        return (
            <div className="flex flex-col self-stretch items-stretch">
                {!hideRates && <TalentRates minimized={minimized}/>}
                <div className={TB.mapClass("self-stretch flex flex-col items-stretch", {
                    "mt-3": !hideRates
                })}>
                    <button
                        disabled={mutatingRequestsRunning > 0}
                        className={TB.mapClass("btn btn-primary", {
                            [className]: true
                        })}
                        onClick={() => {

                            addToProjectModal.current.showModal();
                        }}>
                        Invite to Project
                    </button>
                    <Modal
                        ref={addToProjectModal}
                        modalClass="add-to-project-modal"
                        title="Invite To Project"
                        onCloseAction={() => {
                            addToProjectModal.current.hideModal(true);

                        }}>
                        <AddToProjectModal
                            onInviteSent={async () => {

                                await addToProjectModal.current.hideModal(true);

                                await TB.wait(100);
                                await TB.toast('Invite sent to candidate!');
                            }}
                            isBrowsingTalent={true}
                            isBrowsingTeams={false}
                            talent={talent}/>
                    </Modal>
                </div>

            </div>
        )
    }
    if (isShortlist) {
        console.log('CandidateActions: isShortlist', isShortlist);

        return (
            <ShortlistCandidateActions {...props}
                                       minimized={minimized}
                                       hideRates={hideRates}
                                       className={className}/>
        )
    }
    if (isTalentPool) {
        console.log('CandidateActions: isTalentPool', isTalentPool);

        return (
            <PotentialCandidateActions
                {...props}
                minimized={minimized}
                hideRates={hideRates}
                className={className}/>
        )
    }
    return null;
};

const ShortlistCandidateActions = ({isDisabled: isActionDisabled, className = '', ...props}) => {
    const {project, bulkActions, isProjectUpdating} = useProject();
    const {position} = usePosition();
    const {
        candidate,
        permissionToInvite,
        removeFromCandidates,
        showCandidateProposal,
        addToArchive,
        canArchive,
        assignCandidate,
        unassignCandidate,
        isDisabled: isPositionActionDisabled
    } = useCandidate();
    const {auth} = useAuth();

    const isDisabled = isPositionActionDisabled || isActionDisabled;

    const hasBeenAssigned = useMemo(
        () => !!position?.assigned_candidate?.id, [
            position?.assigned_candidate
        ]);
    const isAssigned = useMemo(
        () => position?.assigned_candidate?.id === candidate?.id, [
            position?.assigned_candidate,
            candidate?.id
        ]);

    const canAssignTalentForProject = auth?.canAssignTalentForProject(project);
    const canRequestInterview = auth?.canRequestTalentInterviewForProject(project);
    const canInvite = auth?.canInviteTalentForProject(project);

    const isActionsDisabled = isDisabled || bulkActions?.length > 0 || isProjectUpdating

    const onCandidateAction = async (request) => {

        let updatedTalent = props.onRequest ? await props.onRequest(request) : await request();

        console.log('onShortlistCandidateAction', updatedTalent)
    }
    if (!candidate?.hasBeenInvited()) {
        console.log('ShortlistedPage Has not been invited');

        return (
            <PotentialCandidateActions{...props} className={className}/>
        )
    }

    const statuses = (
        <React.Fragment>
            {candidate.isPendingShortlisted() && !candidate.shortlistedAccepted() && (
                <CandidateStatusLabel
                    className={props.hideRates ? 'self-center': ' '}
                    status="Waiting for proposal"
                    icon={QuestionCircleFilled}
                    color="#67676a"/>
            )}

            {candidate.declinedPosition() && (
                <CandidateStatusLabel
                    className={props.hideRates ? 'self-center': ' '}
                    status="Declined Project"
                    icon={CloseCircleFilled}
                    color="#67676a"/>
            )}
            {!props.hideRates && candidate.shortlistedAccepted() && (
                <CandidateStatusLabel
                    className={props.hideRates ? 'self-center': ' '}
                    status="Shortlisted"
                    icon={CheckCircleFilled}
                    color={TB.config('color.success-darker')}/>
            )}
        </React.Fragment>
    )

    if (project.isActive()) {
        return null;
    }
    return (
        <div className={TB.mapClass("flex flex-col items-stretch gap-3 candidate-actions", {
            hideRates: props.hideRates
        })}>
            {!props.hideRates && (
                <TalentRates minimized={props.minimized} isCandidate={true}>
                    {statuses}
                </TalentRates>
            )}
            {(props.hideRates || isAssigned || candidate.shortlistedAccepted() || (candidate.declinedPosition() && canArchive && !candidate.job_candidate?.was_disliked) || (candidate.isPendingShortlisted() && !candidate.shortlistedAccepted())) && (
                <div className={TB.mapClass(
                    "potential-candidate-actions shortlisted-candidate-actions",
                    {
                        "p-0 items-stretch gap-3": !props.hideRates,
                        [className]: true,
                    }
                )}>
                    {props.hideRates && statuses}

                    {(candidate.shortlistedAccepted() || isAssigned) && (
                        <React.Fragment>
                            <div
                                className="approved-candidate-actions flex flex-row m-0 gap-3 items-center justify-between"
                                style={{margin: '0px!important'}}>
                                <button
                                    type="button"
                                    style={{margin: 0}}
                                    className={TB.mapClass('btn btn-primary', {
                                        'disabled-edit-link btn-outline-dark': !canRequestInterview || candidate.job_candidate?.has_proposed_interview
                                    })}
                                    disabled={
                                        isActionsDisabled ||
                                        !candidate.job_candidate?.can_request_interview ||
                                        (!isAssigned && hasBeenAssigned)}
                                    onClick={() => {
                                        if (!canRequestInterview) {
                                            return TB.showPermissionError();
                                        }
                                        return showCandidateProposal('&tabView=interviews')
                                    }}
                                >
                                    {candidate.job_candidate?.has_proposed_interview ? 'Interview Requested' : 'Request Interview'}
                                    {!canRequestInterview && <i className="fa fa-lock ml-1"/>}
                                </button>
                                {(isAssigned || !hasBeenAssigned) && (
                                    <button
                                        type="button"
                                        style={{margin: 0}}
                                        className={TB.mapClass('btn', {
                                            'btn-success': isAssigned,
                                            'disabled-edit-link btn-outline-dark': !isAssigned && !canAssignTalentForProject,
                                            'btn-primary': !isAssigned,
                                        })}
                                        disabled={isAssigned ? false : (isActionsDisabled || hasBeenAssigned || !candidate.hasSubmittedProposal())}
                                        onClick={() => {
                                            if (!isAssigned && !canAssignTalentForProject) {
                                                return TB.showPermissionError();
                                            }
                                            if (!isAssigned) {
                                                return onCandidateAction(() => assignCandidate())
                                            }
                                        }}>
                                        {isAssigned ? 'Selected' : 'Select as Hire'}
                                        {!isAssigned && !canAssignTalentForProject && <i className="fa fa-lock ml-1"/>}
                                    </button>
                                )}
                            </div>
                        </React.Fragment>
                    )}
                    {isAssigned ? (
                        <button
                            type="button"
                            style={{margin: 0}}
                            className={TB.mapClass('btn btn-outline-dark', {
                                'disabled-edit-link': !canAssignTalentForProject
                            })}
                            disabled={isActionsDisabled}
                            onClick={() => {
                                if (!canAssignTalentForProject) {
                                    return TB.showPermissionError();
                                }
                                return onCandidateAction(() => unassignCandidate())
                            }}
                        >
                            Reopen Role
                            {!canAssignTalentForProject && <i className="fa fa-lock ml-1"/>}
                        </button>
                    ) : (
                        <React.Fragment>

                            {(candidate.shortlistedAccepted() || (candidate.declinedPosition() && !canArchive && !candidate.job_candidate?.was_disliked)) && (
                                <button
                                    type="button"
                                    className={TB.mapClass('btn btn-outline-dark', {
                                        'disabled-edit-link': !permissionToInvite
                                    })}
                                    disabled={isActionsDisabled}
                                    onClick={async () => {
                                        if (!canInvite) {
                                            return TB.showPermissionError();
                                        }
                                        return onCandidateAction(removeFromCandidates)
                                    }}
                                >
                                    Remove from Shortlist
                                    {!canInvite && <i className="fa fa-lock ml-1"/>}
                                </button>
                            )}
                            {candidate.isPendingShortlisted() && !candidate.shortlistedAccepted() && (
                                <button
                                    type="button"
                                    className={TB.mapClass('btn btn-outline-dark', {
                                        'disabled-edit-link': !permissionToInvite
                                    })}
                                    disabled={isActionsDisabled}
                                    onClick={async () => {
                                        if (!canInvite) {
                                            return TB.showPermissionError();
                                        }
                                        return onCandidateAction(removeFromCandidates)
                                    }}
                                >
                                    Cancel Invite
                                    {!canInvite && <i className="fa fa-lock ml-1"/>}
                                </button>
                            )}

                            {candidate.declinedPosition() && canArchive && !candidate.job_candidate?.was_disliked && (
                                <button
                                    type="button"
                                    className={TB.mapClass('btn btn-outline-dark', {
                                        'disabled-edit-link': !permissionToInvite
                                    })}
                                    disabled={isActionsDisabled}
                                    onClick={async () => {
                                        if (!canInvite) {
                                            return TB.showPermissionError();
                                        }
                                        return onCandidateAction(removeFromCandidates);
                                    }}
                                >
                                    Remove & Archive Candidate
                                    {!canInvite && <i className="fa fa-lock ml-1"/>}
                                </button>
                            )}

                        </React.Fragment>
                    )}

                </div>
            )}

        </div>
    )
}

const PotentialCandidateActions = ({isDisabled: isActionDisabled, className = '', ...props}) => {

    const {bulkActions, isProjectUpdating, project} = useProject();
    const {position} = usePosition();

    const {
        candidate,
        permissionToInvite,
        removeFromCandidates,
        addToCandidates,
        addToArchive,
        canArchive,
        isDisabled: isPositionActionDisabled
    } = useCandidate();
    const isDisabled = isPositionActionDisabled || isActionDisabled;

    const isActionsDisabled = isDisabled || bulkActions?.length > 0 || isProjectUpdating

    const onCandidateAction = async (request) => {

        let updatedTalent = props.onRequest ? await props.onRequest(request) : await request();

        console.log('onPotentialCandidateAction', updatedTalent)
    }

    if (candidate.hasBeenInvited()) {
        const statuses = (
            <React.Fragment>
                {candidate.isPendingShortlisted() && !candidate.shortlistedAccepted() && (
                    <CandidateStatusLabel
                        className={props.hideRates ? 'self-center': ' '}

                        status="Waiting for proposal"
                        icon={QuestionCircleFilled}
                        color="#67676a"/>
                )}

                {candidate.declinedPosition() && (
                    <CandidateStatusLabel
                        className={props.hideRates ? 'self-center': ' '}

                        status="Declined Project"
                        icon={CloseCircleFilled}
                        color="#67676a"/>
                )}
                {candidate.shortlistedAccepted() && (
                    <CandidateStatusLabel
                        className={props.hideRates ? 'self-center': ' '}

                        status="Shortlisted"
                        icon={CheckCircleFilled}
                        color={TB.config('color.success')}/>
                )}
            </React.Fragment>
        )

        if (project.isActive()) {
            return null;
        }
        return (
            <div className={TB.mapClass("candidate-actions", {hideRates: props.hideRates})}>
                {!props.hideRates && (
                    <TalentRates minimized={props.minimized} isCandidate={true}>
                        {statuses}
                    </TalentRates>
                )}

                <div className={TB.mapClass("potential-candidate-actions", {
                    "p-0 items-stretch gap-3 mt-3": !props.hideRates,
                    [className]: true
                })}>
                    {props.hideRates && statuses}

                    {candidate.isAssigned() && (
                        <button
                            type="button"
                            className="btn btn-block btn-success no-click"
                            onClick={() => true}>
                            <span className="position-relative">
                                Selected for Role
                            </span>
                        </button>
                    )}
                    {!candidate.isAssigned() && candidate.shortlistedAccepted() && (
                        <button
                            type="button"
                            className={TB.mapClass('btn btn-outline-dark', {
                                'disabled-edit-link': !permissionToInvite
                            })}
                            disabled={isActionsDisabled}
                            onClick={async () => {
                                if (!permissionToInvite) {
                                    return TB.showPermissionError();
                                }
                                return onCandidateAction(removeFromCandidates)
                            }}
                        >
                            Remove from Shortlist
                            {!permissionToInvite && <i className="fa fa-lock ml-1"/>}
                        </button>
                    )}
                    {!candidate.isAssigned() && candidate.isPendingShortlisted() && !candidate.shortlistedAccepted() && (
                        <button
                            type="button"
                            className={TB.mapClass('btn btn-outline-dark', {
                                'disabled-edit-link': !permissionToInvite
                            })}
                            disabled={isActionsDisabled}
                            onClick={async () => {
                                if (!permissionToInvite) {
                                    return TB.showPermissionError();
                                }
                                return onCandidateAction(removeFromCandidates)
                            }}
                        >
                            Cancel Invite
                            {!permissionToInvite && <i className="fa fa-lock ml-1"/>}
                        </button>
                    )}

                    {!candidate.isAssigned() && candidate.declinedPosition() && !candidate.job_candidate?.was_disliked && (
                        <button
                            type="button"
                            className={TB.mapClass('btn btn-outline-dark', {
                                'disabled-edit-link': !permissionToInvite
                            })}
                            disabled={isActionsDisabled}
                            onClick={async () => {
                                if (!permissionToInvite) {
                                    return TB.showPermissionError();
                                }
                                return onCandidateAction(removeFromCandidates);
                            }}
                        >
                            Remove & Archive Candidate
                            {!permissionToInvite && <i className="fa fa-lock ml-1"/>}
                        </button>
                    )}

                </div>

            </div>
        );
    }

    if (project.isActive()) {
        return null;
    }
    return (
        <div className={TB.mapClass("candidate-actions", {hideRates: props.hideRates})}>
            {!props.hideRates && (
                <TalentRates minimized={props.minimized} isCandidate={true}/>
            )}
            <div className={TB.mapClass("potential-candidate-actions", {
                "p-0 items-stretch gap-3": !props.hideRates,
                [className]: true
            })}>

                <div className="flex flex-col">
                    <button
                        type="button"
                        onClick={() => onCandidateAction(addToCandidates)}
                        disabled={isActionsDisabled}
                        className="btn btn-primary block"
                    >
                        {candidate.matchStatusIsInterested() ? (
                            <span className="text-sm">
                                Add to Shortlist
                                {!permissionToInvite && (
                                    <i className="fa fa-lock" style={styles.inviteLockIcon}/>
                                )}
                            </span>
                        ) : (
                            <span className="text-sm">
                                Invite To Propose
                                {!permissionToInvite && (
                                    <i className="fa fa-lock" style={styles.inviteLockIcon}/>
                                )}
                            </span>
                        )}
                        {candidate.matchStatusIsInterested() ? (
                            <p className="text-xxs text-center text-white">
                                (candidate will be notified)
                            </p>
                        ) : (
                            <p className="text-xxs text-center text-white">
                                (candidate will be contacted to review brief)
                            </p>
                        )}
                    </button>

                </div>

            </div>
        </div>
    )
}


export default CandidateActions;

const styles = {
    favoriteBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-end'
    },

    archiveBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-start'
    },
    inviteLockIcon: {
        position: 'absolute',
        fontSize: 13,
        right: -18,
    }
};